import type { FunctionComponent } from 'react';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import cx from 'classnames';

import type { SourceType } from '@trello/analytics-types';
import { Analytics } from '@trello/atlassian-analytics';
import { dontUpsell } from '@trello/browser';

import { useFreeTrialEligibilityRules } from 'app/src/components/FreeTrial';
import { Content } from './Content';
import { OverLimitButton } from './OverLimitButton';
import userLimitsIcon from './userLimitsIcon.svg';

import styles from './BoardWouldPushWorkspaceOverLimit.less';

export type Action = 'copy' | 'move' | 'reopen' | 'share';

export interface BoardWouldPushWorkspaceOverLimitProps {
  workspaceId: string;
  action: Action;
  className?: string;
  onCtaClick?: () => void;
  source?: SourceType;
}

export const BoardWouldPushWorkspaceOverLimit: FunctionComponent<
  BoardWouldPushWorkspaceOverLimitProps
> = ({ workspaceId, action, className = '', onCtaClick, source }) => {
  const { isEligible = false, loading } =
    useFreeTrialEligibilityRules(workspaceId);

  useEffect(() => {
    Analytics.sendViewedComponentEvent({
      componentName: 'workspaceChooserUserLimitExceededMessage',
      componentType: 'workspaceChooserMessage',
      source: 'workspaceChooserPopover',
      attributes: { workspaceId },
    });
  }, [workspaceId]);

  if (loading) {
    return null;
  }

  return (
    <div
      className={cx({
        [styles.container]: true,
        [styles.canUpgrade]: isEligible || !dontUpsell(),
        [className]: !!className,
      })}
    >
      <img src={userLimitsIcon} alt="" />
      <h3>
        <FormattedMessage
          id="templates.board_closed.need-more-space"
          defaultMessage="Looks like you need more space!"
          description="Looks like you need more space!"
        />
      </h3>
      <p>
        <Content action={action} isFreeTrialEligible={isEligible} />
      </p>
      <OverLimitButton
        workspaceId={workspaceId}
        isFreeTrialEligible={isEligible}
        onCtaClick={onCtaClick}
        source={source}
      />
    </div>
  );
};
